import React, { useState } from 'react';
import { FaBook, FaFileAlt, FaPencilAlt, FaCalendarAlt, FaCheckCircle, FaDownload, FaUserGraduate, FaClipboardList, FaGraduationCap, FaChartLine, FaLightbulb, FaTrophy, FaUsers, FaClock, FaRupeeSign, FaFlask, FaCalculator, FaLanguage, FaGlobe, FaMedal, FaStar, FaBrain } from 'react-icons/fa';

const OlympiadProgramsPage = () => {
  const [selectedOlympiad, setSelectedOlympiad] = useState('IMO');

  const olympiadData = {
    'IMO': {
      name: "International Mathematics Olympiad",
      fullForm: "IMO - Science Olympiad Foundation",
      icon: <FaCalculator />,
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      description: "IMO focuses on enhancing mathematical skills, logical reasoning, and problem-solving abilities",
      classes: "1st to 12th",
      sections: [
        { name: "Logical Reasoning", weightage: "20%" },
        { name: "Mathematical Reasoning", weightage: "40%" },
        { name: "Everyday Mathematics", weightage: "30%" },
        { name: "Achievers Section", weightage: "10%" }
      ],
      syllabus: {
        "Classes 1-2": ["Number Sense", "Computation", "Shapes & Patterns", "Measurement", "Money"],
        "Classes 3-5": ["Number System", "Fractions", "Geometry", "Data Handling", "Logical Reasoning"],
        "Classes 6-8": ["Algebra", "Geometry", "Mensuration", "Data Handling", "Number Theory"],
        "Classes 9-10": ["Real Numbers", "Polynomials", "Coordinate Geometry", "Trigonometry", "Statistics & Probability"],
        "Classes 11-12": ["Calculus", "3D Geometry", "Vectors", "Complex Numbers", "Probability Distributions"]
      }
    },
    'NSO': {
      name: "National Science Olympiad",
      fullForm: "NSO - Science Olympiad Foundation",
      icon: <FaFlask />,
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      description: "NSO aims to identify and nurture future scientists, engineers and doctors through science learning",
      classes: "1st to 12th",
      sections: [
        { name: "Logical Reasoning", weightage: "20%" },
        { name: "Science (Physics, Chemistry, Biology)", weightage: "70%" },
        { name: "Achievers Section", weightage: "10%" }
      ],
      syllabus: {
        "Classes 1-2": ["Plants & Animals", "Human Body", "Food", "Water", "Air", "Weather"],
        "Classes 3-5": ["Living & Non-living", "Animals", "Plants", "Food & Health", "Matter", "Force & Energy"],
        "Classes 6-8": ["Physics: Motion, Force, Light, Sound", "Chemistry: Matter, Atoms, Reactions", "Biology: Cell, Nutrition, Reproduction"],
        "Classes 9-10": ["Physics: Laws of Motion, Gravitation, Energy", "Chemistry: Chemical Bonding, Acids", "Biology: Life Processes, Heredity"],
        "Classes 11-12": ["Physics: Mechanics, Thermodynamics, Electromagnetism", "Chemistry: Physical, Organic, Inorganic", "Biology: Genetics, Ecology, Biotechnology"]
      }
    },
    'IEO': {
      name: "International English Olympiad",
      fullForm: "IEO - Science Olympiad Foundation",
      icon: <FaLanguage />,
      color: "#43e97b",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      description: "IEO assesses English language proficiency, vocabulary, grammar, and comprehension skills",
      classes: "1st to 12th",
      sections: [
        { name: "Word & Structure Knowledge", weightage: "35%" },
        { name: "Reading", weightage: "35%" },
        { name: "Spoken & Written Expression", weightage: "20%" },
        { name: "Achievers Section", weightage: "10%" }
      ],
      syllabus: {
        "Classes 1-2": ["Alphabet", "Rhyming Words", "Singular-Plural", "Articles", "Simple Sentences"],
        "Classes 3-5": ["Nouns", "Pronouns", "Verbs", "Adjectives", "Prepositions", "Tenses", "Comprehension"],
        "Classes 6-8": ["Parts of Speech", "Active-Passive", "Direct-Indirect", "Idioms", "Comprehension"],
        "Classes 9-10": ["Advanced Grammar", "Vocabulary", "Reading Comprehension", "Writing Skills", "Literary Devices"],
        "Classes 11-12": ["Business English", "Formal Writing", "Critical Reading", "Advanced Composition", "Literature"]
      }
    },
    'ISSO': {
      name: "International Social Studies Olympiad",
      fullForm: "ISSO - Science Olympiad Foundation",
      icon: <FaGlobe />,
      color: "#ffa502",
      gradient: "linear-gradient(135deg, #ffa502 0%, #ff6348 100%)",
      description: "ISSO promotes awareness of social sciences, geography, history, civics and current affairs",
      classes: "3rd to 10th",
      sections: [
        { name: "History", weightage: "30%" },
        { name: "Geography", weightage: "30%" },
        { name: "Civics/Political Science", weightage: "25%" },
        { name: "Achievers Section", weightage: "15%" }
      ],
      syllabus: {
        "Classes 3-5": ["Our Environment", "Maps", "Earth & Solar System", "Transport", "Communication", "Government"],
        "Classes 6-8": ["History: Ancient to Medieval", "Geography: Earth Features, Climate", "Civics: Government, Constitution"],
        "Classes 9-10": ["History: Modern India, World Wars", "Geography: Resources, Agriculture, Industries", "Civics: Democracy, Political Parties", "Economics: Development, Money"]
      }
    }
  };

  const benefits = [
    { icon: <FaTrophy />, title: "International Recognition", desc: "Certificates & medals recognized worldwide" },
    { icon: <FaBrain />, title: "Mental Development", desc: "Enhances analytical & logical thinking" },
    { icon: <FaMedal />, title: "Scholarships", desc: "Cash prizes & scholarships for top performers" },
    { icon: <FaStar />, title: "Competitive Edge", desc: "Better performance in school & entrance exams" },
    { icon: <FaGraduationCap />, title: "Conceptual Learning", desc: "Deep understanding of subjects" },
    { icon: <FaUsers />, title: "Confidence Building", desc: "Boosts self-confidence & motivation" }
  ];

  const examStructure = [
    { level: "Level 1", desc: "School Level Examination conducted in schools", months: "November - December" },
    { level: "Level 2", desc: "International Level for Level 1 qualifiers", months: "February - March" },
    { level: "Results", desc: "Results declared within 8-10 weeks", months: "Post Examination" }
  ];

  const registrationSteps = [
    { step: 1, title: "School Registration", desc: "Schools register students for the exam" },
    { step: 2, title: "Fee Payment", desc: "Pay registration fee through school" },
    { step: 3, title: "Roll Number", desc: "Receive roll number from school" },
    { step: 4, title: "Appear for Exam", desc: "Take exam at your school on scheduled date" }
  ];

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        padding: '80px 20px 60px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>
            <FaTrophy />
          </div>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '20px' }}>
            Olympiad Programs
          </h1>
          <p style={{ fontSize: '1.3rem', marginBottom: '15px', opacity: 0.95 }}>
            IMO • NSO • IEO • ISSO
          </p>
          <p style={{ fontSize: '1.2rem', marginBottom: '30px', opacity: 0.9 }}>
            Building Excellence in Science, Mathematics, English & Social Studies
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.href = '/register'}
              style={{
                background: 'white',
                color: '#667eea',
                padding: '15px 40px',
                borderRadius: '30px',
                border: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Enroll Now
            </button>
            <button
              onClick={() => document.getElementById('syllabus').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '15px 40px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}
            >
              View Syllabus
            </button>
          </div>
        </div>
      </div>

      {/* Olympiad Selection Tabs */}
      <div style={{ 
        background: 'white', 
        padding: '30px 20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        position: 'sticky',
        top: '0',
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
          }}>
            {['IMO', 'NSO', 'IEO', 'ISSO'].map(olymp => (
              <button
                key={olymp}
                onClick={() => setSelectedOlympiad(olymp)}
                style={{
                  padding: '15px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  background: selectedOlympiad === olymp 
                    ? olympiadData[olymp].gradient
                    : '#f0f0f0',
                  color: selectedOlympiad === olymp ? 'white' : '#333',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedOlympiad === olymp ? `0 4px 15px ${olympiadData[olymp].color}40` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  if (selectedOlympiad !== olymp) {
                    e.currentTarget.style.background = '#e0e0e0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOlympiad !== olymp) {
                    e.currentTarget.style.background = '#f0f0f0';
                  }
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>{olympiadData[olymp].icon}</span>
                {olymp}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        
        {/* Overview Section */}
        <section style={{ marginBottom: '60px' }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            border: `3px solid ${olympiadData[selectedOlympiad].color}`,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              fontSize: '15rem',
              opacity: 0.05,
              color: olympiadData[selectedOlympiad].color
            }}>
              {olympiadData[selectedOlympiad].icon}
            </div>
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ 
                fontSize: '3.5rem', 
                color: olympiadData[selectedOlympiad].color,
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                {olympiadData[selectedOlympiad].icon}
              </div>
              
              <h2 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                marginBottom: '10px',
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                {olympiadData[selectedOlympiad].name}
              </h2>
              
              <p style={{ 
                fontSize: '1.1rem',
                color: '#666',
                textAlign: 'center',
                marginBottom: '30px',
                fontStyle: 'italic'
              }}>
                {olympiadData[selectedOlympiad].fullForm}
              </p>

              <p style={{ 
                fontSize: '1.2rem',
                color: '#555',
                textAlign: 'center',
                lineHeight: '1.8',
                marginBottom: '30px',
                maxWidth: '900px',
                margin: '0 auto 30px'
              }}>
                {olympiadData[selectedOlympiad].description}
              </p>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '30px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  padding: '20px 30px',
                  background: olympiadData[selectedOlympiad].gradient,
                  borderRadius: '15px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1rem', opacity: 0.9 }}>Applicable for</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginTop: '5px' }}>
                    {olympiadData[selectedOlympiad].classes}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Why Participate in Olympiads?
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '35px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${olympiadData[selectedOlympiad].color}30`;
                e.currentTarget.style.borderColor = olympiadData[selectedOlympiad].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'transparent';
              }}>
                <div style={{ 
                  fontSize: '3.5rem',
                  color: olympiadData[selectedOlympiad].color,
                  marginBottom: '20px'
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{ 
                  fontSize: '1.4rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: '#2c3e50'
                }}>
                  {benefit.title}
                </h3>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.6',
                  margin: 0,
                  fontSize: '1rem'
                }}>
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Exam Pattern */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            <FaClipboardList style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
            Exam Pattern - {selectedOlympiad}
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '40px'
            }}>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: olympiadData[selectedOlympiad].color }}>
                  60 Min
                </div>
                <div style={{ color: '#666', marginTop: '5px' }}>Duration</div>
              </div>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: olympiadData[selectedOlympiad].color }}>
                  50
                </div>
                <div style={{ color: '#666', marginTop: '5px' }}>Questions</div>
              </div>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: olympiadData[selectedOlympiad].color }}>
                  MCQ
                </div>
                <div style={{ color: '#666', marginTop: '5px' }}>Type</div>
              </div>
            </div>

            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '20px', color: '#2c3e50' }}>
              Section-wise Distribution
            </h3>

            <div style={{ 
              display: 'grid',
              gap: '15px'
            }}>
              {olympiadData[selectedOlympiad].sections.map((section, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  background: `${olympiadData[selectedOlympiad].color}10`,
                  borderRadius: '12px',
                  border: `2px solid ${olympiadData[selectedOlympiad].color}30`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${olympiadData[selectedOlympiad].color}20`;
                  e.currentTarget.style.transform = 'translateX(10px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `${olympiadData[selectedOlympiad].color}10`;
                  e.currentTarget.style.transform = 'translateX(0)';
                }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2c3e50' }}>
                    {section.name}
                  </span>
                  <span style={{ 
                    fontSize: '1.3rem', 
                    fontWeight: 'bold',
                    color: olympiadData[selectedOlympiad].color,
                    padding: '5px 15px',
                    background: 'white',
                    borderRadius: '20px'
                  }}>
                    {section.weightage}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Syllabus Section */}
        <section id="syllabus" style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            <FaBook style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
            Detailed Syllabus - {selectedOlympiad}
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '25px'
          }}>
            {Object.entries(olympiadData[selectedOlympiad].syllabus).map(([grade, topics], idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '30px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                border: `2px solid ${olympiadData[selectedOlympiad].color}20`,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = olympiadData[selectedOlympiad].color;
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${olympiadData[selectedOlympiad].color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${olympiadData[selectedOlympiad].color}20`;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold', 
                  marginBottom: '20px',
                  color: olympiadData[selectedOlympiad].color,
                  borderBottom: `3px solid ${olympiadData[selectedOlympiad].color}`,
                  paddingBottom: '10px'
                }}>
                  {grade}
                </h3>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0,
                  margin: 0
                }}>
                  {topics.map((topic, i) => (
                    <li key={i} style={{ 
                      padding: '10px 0',
                      borderBottom: i < topics.length - 1 ? '1px solid #f0f0f0' : 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      color: '#555'
                    }}>
                      <FaCheckCircle style={{ color: olympiadData[selectedOlympiad].color, marginTop: '4px', flexShrink: 0 }} />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Exam Structure */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Exam Structure & Timeline
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '25px'
            }}>
              {examStructure.map((level, idx) => (
                <div key={idx} style={{
                  padding: '30px',
                  background: olympiadData[selectedOlympiad].gradient,
                  borderRadius: '15px',
                  color: 'white',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <div style={{ 
                    fontSize: '2.5rem',
                    marginBottom: '15px',
                    fontWeight: 'bold'
                  }}>
                    {idx + 1}
                  </div>
                  <h3 style={{ 
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    {level.level}
                  </h3>
                  <p style={{ 
                    marginBottom: '10px',
                    opacity: 0.95,
                    lineHeight: '1.6'
                  }}>
                    {level.desc}
                  </p>
                  <div style={{
                    marginTop: '15px',
                    padding: '10px',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}>
                    {level.months}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Registration Process */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            How to Register?
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '30px'
            }}>
              {registrationSteps.map((step, idx) => (
                <div key={idx} style={{
                  position: 'relative',
                  padding: '30px 20px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto 20px',
                    borderRadius: '50%',
                    background: olympiadData[selectedOlympiad].gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    color: 'white',
                    boxShadow: `0 8px 20px ${olympiadData[selectedOlympiad].color}40`,
                    transition: 'all 0.3s ease'
                  }}>
                    {step.step}
                  </div>
                  <h4 style={{
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    color: '#2c3e50'
                  }}>
                    {step.title}
                  </h4>
                  <p style={{
                    color: '#666',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Important Dates - COMPLETE THIS SECTION */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            <FaCalendarAlt style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
            Important Dates 2024-25
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ overflow: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '1.1rem'
              }}>
                <thead>
                  <tr style={{ background: olympiadData[selectedOlympiad].gradient, color: 'white' }}>
                    <th style={{ padding: '20px', textAlign: 'left', borderRadius: '8px 0 0 0' }}>Event</th>
                    <th style={{ padding: '20px', textAlign: 'right', borderRadius: '0 8px 0 0' }}>Date/Month</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { event: "Registration Opens", date: "July - August" },
                    { event: "Last Date for Registration", date: "October - November" },
                    { event: "Level 1 Exam Date", date: "November - December" },
                    { event: "Level 1 Result", date: "January - February" },
                    { event: "Level 2 Exam Date", date: "February - March" },
                    { event: "Level 2 Result", date: "April - May" }
                  ].map((item, idx) => (
                    <tr key={idx} style={{ 
                      background: idx % 2 === 0 ? '#f8f9fa' : 'white',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <td style={{ padding: '20px', fontWeight: '600', color: '#2c3e50' }}>
                        <FaCalendarAlt style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
                        {item.event}
                      </td>
                      <td style={{ padding: '20px', textAlign: 'right', color: '#666' }}>
                        {item.date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Sample Papers */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            <FaFileAlt style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
            Previous Year Papers & Sample Papers
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {['Class 1-2', 'Class 3-5', 'Class 6-8', 'Class 9-10', 'Class 11-12'].map((cls) => (
                <button key={cls} style={{
                  padding: '25px',
                  borderRadius: '12px',
                  border: '2px solid #e8e8e8',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = olympiadData[selectedOlympiad].gradient;
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#2c3e50';
                  e.currentTarget.style.borderColor = '#e8e8e8';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <span>{selectedOlympiad} - {cls}</span>
                  <FaDownload />
                </button>
              ))}
            </div>
            <p style={{ 
              textAlign: 'center', 
              marginTop: '30px',
              color: '#666',
              fontSize: '1rem'
            }}>
              Download previous year papers and practice for better preparation
            </p>
          </div>
        </section>

        {/* Preparation Tips */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            <FaLightbulb style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
            Preparation Strategy
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '25px'
            }}>
              {[
                {
                  title: "Master Fundamentals",
                  desc: "Build strong foundation in core concepts",
                  icon: <FaBook />
                },
                {
                  title: "Practice Regularly",
                  desc: "Solve previous year papers & mock tests",
                  icon: <FaPencilAlt />
                },
                {
                  title: "Time Management",
                  desc: "Practice solving papers within time limit",
                  icon: <FaClock />
                },
                {
                  title: "Focus on Reasoning",
                  desc: "Enhance logical and analytical thinking",
                  icon: <FaBrain />
                },
                {
                  title: "Revise Thoroughly",
                  desc: "Regular revision of all topics",
                  icon: <FaBook />
                },
                {
                  title: "Stay Updated",
                  desc: "Keep track of current affairs and GK",
                  icon: <FaGlobe />
                }
              ].map((tip, idx) => (
                <div key={idx} style={{
                  padding: '25px',
                  border: '2px solid #e8e8e8',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = olympiadData[selectedOlympiad].color;
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${olympiadData[selectedOlympiad].color}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e8e8e8';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <div style={{ 
                    fontSize: '2.5rem',
                    color: olympiadData[selectedOlympiad].color,
                    marginBottom: '15px'
                  }}>
                    {tip.icon}
                  </div>
                  <h4 style={{ 
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    color: '#2c3e50'
                  }}>
                    {tip.title}
                  </h4>
                  <p style={{ 
                    color: '#666',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Awards & Recognition */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            <FaMedal style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
            Awards & Recognition
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                rank: "International Rank 1-3",
                award: "Gold Medal + Certificate + Cash Prize",
                gradient: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
              },
              {
                rank: "Zonal Rank 1-25",
                award: "Gifts + Medal + Certificate",
                gradient: "linear-gradient(135deg, #C0C0C0 0%, #808080 100%)"
              },
              {
                rank: "School Rank 1-10",
                award: "Certificate of Excellence",
                gradient: "linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)"
              },
              {
                rank: "Top 5% Students",
                award: "Certificate of Distinction",
                gradient: olympiadData[selectedOlympiad].gradient
              }
            ].map((award, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                <div style={{
                  background: award.gradient,
                  padding: '25px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <FaMedal style={{ fontSize: '3rem', marginBottom: '10px' }} />
                  <h3 style={{ 
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    margin: 0
                  }}>
                    {award.rank}
                  </h3>
                </div>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p style={{ 
                    fontSize: '1rem',
                    color: '#666',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {award.award}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Course Packages */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            <FaRupeeSign style={{ marginRight: '10px', color: olympiadData[selectedOlympiad].color }} />
            Course Packages
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                name: "Single Olympiad",
                price: "3,499",
                duration: "6 Months",
                features: [
                  "One Olympiad Preparation",
                  "Study Material",
                  "10 Mock Tests",
                  "Video Lectures",
                  "Doubt Support"
                ],
                gradient: olympiadData[selectedOlympiad].gradient
              },
              {
                name: "Combo - 2 Olympiads",
                price: "5,999",
                duration: "6 Months",
                features: [
                  "Any Two Olympiads",
                  "Complete Study Material",
                  "20 Mock Tests",
                  "Video Lectures",
                  "Live Classes",
                  "WhatsApp Support"
                ],
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                popular: true
              },
              {
                name: "All 4 Olympiads",
                price: "9,999",
                duration: "12 Months",
                features: [
                  "All 4 Olympiads",
                  "Premium Study Material",
                  "Unlimited Mock Tests",
                  "Live Classes",
                  "Personal Mentor",
                  "Performance Analysis",
                  "24/7 Support"
                ],
                gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              }
            ].map((pkg, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)',
                border: pkg.popular ? '3px solid #667eea' : '1px solid #e8e8e8',
                position: 'relative',
                transform: pkg.popular ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.08)' : 'scale(1.03)';
                e.currentTarget.style.boxShadow = '0 15px 45px rgba(102,126,234,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.05)' : 'scale(1)';
                e.currentTarget.style.boxShadow = pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '-35px',
                    background: '#FFD700',
                    color: '#333',
                    padding: '5px 40px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                    zIndex: 1
                  }}>
                    POPULAR
                  </div>
                )}
                
                <div style={{
                  background: pkg.gradient,
                  padding: '40px 30px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <h3 style={{ 
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    {pkg.name}
                  </h3>
                  <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '10px' }}>
                    ₹{pkg.price}
                  </div>
                  <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>
                    {pkg.duration}
                  </div>
                </div>

                <div style={{ padding: '30px' }}>
                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 25px 0'
                  }}>
                    {pkg.features.map((feature, i) => (
                      <li key={i} style={{
                        padding: '12px 0',
                        borderBottom: i < pkg.features.length - 1 ? '1px solid #f0f0f0' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#555'
                      }}>
                        <FaCheckCircle style={{ color: '#43e97b', fontSize: '1.2rem', flexShrink: 0 }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '10px',
                    border: 'none',
                    background: pkg.gradient,
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => window.location.href = '/register'}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{
            background: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            {[
              {
                q: "What are Olympiad exams?",
                a: "Olympiad exams are competitive examinations conducted by Science Olympiad Foundation (SOF) to identify and nurture talented students in Mathematics, Science, English, and Social Studies."
              },
              {
                q: "Who can participate in Olympiads?",
                a: "Students from Class 1 to 12 can participate. The exam difficulty level varies according to the class."
              },
              {
                q: "How to register for Olympiad exams?",
                a: "Registration is done through schools. Students need to contact their school coordinators and pay the registration fee."
              },
              {
                q: "What is the exam fee?",
                a: "The registration fee is approximately ₹125-150 per Olympiad (varies by school and location)."
              },
              {
                q: "When are Olympiad exams conducted?",
                a: "Level 1 exams are conducted in November-December, and Level 2 exams are held in February-March every year."
              },
              {
                q: "What are the benefits of Olympiad exams?",
                a: "Benefits include international recognition, medals & certificates, cash prizes, scholarships, enhanced learning, and better performance in school and competitive exams."
              },
              {
                q: "How are Olympiad winners selected?",
                a: "Winners are selected based on their performance at international, zonal, and school levels. Top performers receive medals, certificates, and cash prizes."
              },
              {
                q: "Can students from any board participate?",
                a: "Yes, students from CBSE, ICSE, State Boards, and International Boards can all participate in Olympiad exams."
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                padding: '30px',
                borderBottom: idx < 7 ? '1px solid #e8e8e8' : 'none'
              }}>
                <h4 style={{ 
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: olympiadData[selectedOlympiad].color,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <span style={{ flexShrink: 0 }}>Q{idx + 1}.</span>
                  <span>{faq.q}</span>
                </h4>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.8',
                  margin: '0 0 0 35px',
                  fontSize: '1.05rem'
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div style={{
            background: olympiadData[selectedOlympiad].gradient,
            padding: '60px 40px',
            borderRadius: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>
              <FaTrophy />
            </div>
            <h2 style={{ 
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              Ready to Excel in Olympiads?
            </h2>
            <p style={{ 
              fontSize: '1.3rem',
              marginBottom: '35px',
              opacity: 0.95,
              maxWidth: '700px',
              margin: '0 auto 35px'
            }}>
              Join thousands of students building excellence through Olympiad preparation
            </p>
            <div style={{ 
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={() => window.location.href = '/register'}
                style={{
                  background: 'white',
                  color: olympiadData[selectedOlympiad].color,
                  padding: '18px 45px',
                  borderRadius: '30px',
                  border: 'none',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                }}>
                Start Your Olympiad Journey
              </button>
              <button style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '18px 45px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => window.location.href = '/contact'}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = olympiadData[selectedOlympiad].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}>
                Get Free Demo
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer Note */}
      <div style={{
        background: '#2c3e50',
        color: 'white',
        padding: '30px 20px',
        textAlign: 'center',
        marginTop: '60px'
      }}>
        <p style={{ margin: '0 0 10px', fontSize: '1.1rem' }}>
          © 2025 Olewin Academy - All Rights Reserved
        </p>
        <p style={{ margin: 0, opacity: 0.8 }}>
          For official information, visit: <a href="https://www.sofworld.org" target="_blank" rel="noopener noreferrer" style={{ color: '#43e97b', textDecoration: 'none' }}>sofworld.org</a>
        </p>
      </div>
    </div>
  );
};

export default OlympiadProgramsPage;